import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Accordion, Card, Spinner } from 'react-bootstrap';
import { showToast as toast } from '../toastManager';
import axios from 'axios';

const BusinessContextAndAssets = ({ ViewedPage, shop_domain, app_url, isInSettingsContainer }) => {
  useEffect(() => {
    ViewedPage(45);
  }, []);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [businessContext, setBusinessContext] = useState('');

  // Per-page context state
  const [perPageMap, setPerPageMap]         = useState({});
  const [websitePages, setWebsitePages]     = useState([]);
  const [landingPages, setLandingPages]     = useState([]);
  const [selectedPage, setSelectedPage]     = useState('');
  const [contextDraft, setContextDraft]     = useState('');
  const [perPageLoading, setPerPageLoading] = useState(false);
  const [perPageSaving, setPerPageSaving]   = useState(false);

  const loadBusinessContext = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${app_url}/api/settings/business-context`, { withCredentials: true });
      if (response.data.success) {
        setBusinessContext(response.data.data || '');
      }
    } catch (error) {
      console.error('Error loading business context:', error);
      toast.error('Error loading business context.');
    } finally {
      setLoading(false);
    }
  };

  const loadPerPage = async () => {
    try {
      setPerPageLoading(true);
      const r = await axios.get(`${app_url}/api/settings/business-context/per-page`, { withCredentials: true });
      if (r.data.success) {
        setPerPageMap(r.data.per_page || {});
        setWebsitePages(r.data.website_pages || []);
        setLandingPages(r.data.landing_pages || []);
      }
    } catch (error) {
      console.error('Error loading per-page context:', error);
    } finally {
      setPerPageLoading(false);
    }
  };

  useEffect(() => {
    if (shop_domain && app_url) {
      loadBusinessContext();
      loadPerPage();
    }
  }, [shop_domain, app_url]);

  useEffect(() => {
    setContextDraft(perPageMap[selectedPage] || '');
  }, [selectedPage, perPageMap]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await axios.post(`${app_url}/api/settings/business-context/save`, {
        business_context: businessContext
      }, { withCredentials: true });
      if (response.data.success) {
        toast.success('Business context saved successfully');
      } else {
        toast.error(response.data.message || 'Failed to save business context');
      }
    } catch (error) {
      console.error('Error saving business context:', error);
      toast.error('Error saving business context. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePageSave = async () => {
    if (!selectedPage) return;
    try {
      setPerPageSaving(true);
      const r = await axios.post(`${app_url}/api/settings/business-context/per-page`, {
        url: selectedPage,
        context: contextDraft,
      }, { withCredentials: true });
      if (r.data.success) {
        setPerPageMap(prev => ({ ...prev, [selectedPage]: contextDraft }));
        toast.success('Page context saved');
      } else {
        toast.error(r.data.message || 'Failed to save page context');
      }
    } catch (error) {
      console.error('Error saving page context:', error);
      toast.error('Error saving page context. Please try again.');
    } finally {
      setPerPageSaving(false);
    }
  };

  const noPagesSelected = websitePages.length === 0 && landingPages.length === 0;

  return (
    <Container fluid className="p-0">
      <div className='main-title mt-4'>
        <h2>Business Context</h2>
      </div>
      <Row>
        <Col>
          <Accordion defaultActiveKey="edit" className="mt-4" alwaysOpen>
            <Accordion.Item eventKey="edit">
              <Accordion.Header><strong>Business Context</strong></Accordion.Header>
              <Accordion.Body>
                <Card className="asset-form-card">
                  <Card.Body>
                    {loading ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                      </div>
                    ) : (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>Edit business context below</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={12}
                            value={businessContext}
                            onChange={(e) => setBusinessContext(e.target.value)}
                            placeholder="Enter business context..."
                            style={{ fontFamily: 'inherit', resize: 'vertical' }}
                          />
                        </Form.Group>
                        <Button
                          variant="primary"
                          disabled={saving}
                          onClick={handleSave}
                          style={{ backgroundColor: 'var(--cux-btn, #2b4570)', borderColor: 'var(--cux-btn, #2b4570)' }}
                        >
                          {saving ? <><Spinner size="sm" animation="border" className="me-2" />Saving...</> : 'Save'}
                        </Button>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="per-page">
              <Accordion.Header><strong>Page Context</strong></Accordion.Header>
              <Accordion.Body>
                <Card className="asset-form-card">
                  <Card.Body>
                    {perPageLoading ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                      </div>
                    ) : noPagesSelected ? (
                      <p className="text-muted mb-0">
                        No pages selected — set them up in Domains &amp; URLs first.
                      </p>
                    ) : (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>Select a page</Form.Label>
                          <Form.Select
                            value={selectedPage}
                            onChange={(e) => setSelectedPage(e.target.value)}
                          >
                            <option value="">— choose a page —</option>
                            {websitePages.length > 0 && (
                              <optgroup label="Website Pages">
                                {websitePages.map((u) => (
                                  <option key={u} value={u}>{u}</option>
                                ))}
                              </optgroup>
                            )}
                            {landingPages.length > 0 && (
                              <optgroup label="Landing Pages">
                                {landingPages.map((u) => (
                                  <option key={u} value={u}>{u}</option>
                                ))}
                              </optgroup>
                            )}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Context for the selected page</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={10}
                            value={contextDraft}
                            onChange={(e) => setContextDraft(e.target.value)}
                            placeholder={selectedPage ? 'Enter context for this page...' : 'Select a page above to begin'}
                            disabled={!selectedPage}
                            style={{ fontFamily: 'inherit', resize: 'vertical' }}
                          />
                        </Form.Group>

                        <Button
                          variant="primary"
                          disabled={!selectedPage || perPageSaving}
                          onClick={handlePageSave}
                          style={{ backgroundColor: 'var(--cux-btn, #2b4570)', borderColor: 'var(--cux-btn, #2b4570)' }}
                        >
                          {perPageSaving ? <><Spinner size="sm" animation="border" className="me-2" />Saving...</> : 'Save'}
                        </Button>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Col>
      </Row>
    </Container>
  );
};

export default BusinessContextAndAssets;
